// <pfs-system-status-entry>: pollt data-url, zeigt Display-Name + Version + Status-Dot.
//
// Response-Shape (Konvention, matcht mednet-api/manifest.go):
//   { applicationName, applicationVersion, status? }

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { apiFetch, ApiError } from "../../store/api-fetch.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";

interface ManifestResponse {
  applicationName?: string;
  applicationVersion?: string;
  version?: string;
  status?: string;
  [key: string]: unknown;
}

const REFRESH_EVENT = "pfs:refresh";

type Status = "loading" | "ok" | "error";

defineComponent({
  tagName: "pfs-system-status-entry",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;

    const url = host.getAttribute("data-url") ?? "";
    const name = host.getAttribute("data-name") ?? "unknown";
    const displayName = host.getAttribute("data-display-name") ?? name;
    const kind = host.getAttribute("data-kind") ?? "";

    const root = document.createElement("div");
    root.classList.add("pfs-entry");
    root.dataset.kind = kind;

    const dot = document.createElement("span");
    dot.classList.add("pfs-entry__dot");

    const label = document.createElement("span");
    label.classList.add("pfs-entry__label");
    label.textContent = displayName;

    const versionEl = document.createElement("span");
    versionEl.classList.add("pfs-entry__version");
    versionEl.textContent = "—";

    root.appendChild(dot);
    root.appendChild(label);
    root.appendChild(versionEl);

    const style = document.createElement("style");
    style.textContent = STYLES;
    ctx.shadowRoot.appendChild(style);
    ctx.shadowRoot.appendChild(root);

    const setStatus = (status: Status, version?: string) => {
      root.dataset.status = status;
      versionEl.textContent = version ?? (status === "loading" ? "…" : "—");
    };

    const fetchOnce = async () => {
      if (!url) {
        setStatus("error");
        return;
      }
      setStatus("loading");
      try {
        const data = await apiFetch<ManifestResponse>(url);
        const version = data.applicationVersion ?? data.version;
        setStatus("ok", version);
      } catch (err) {
        const status = err instanceof ApiError ? err.status : 0;
        console.warn(`[pfs-system-status-entry] ${name} fetch failed`, status, err);
        setStatus("error");
      }
    };

    fetchOnce();

    const onRefresh = () => fetchOnce();
    host.addEventListener(REFRESH_EVENT, onRefresh);
    ctx.cleanup.push(() => host.removeEventListener(REFRESH_EVENT, onRefresh));
  },
});

const STYLES = `
${SHADOW_RESET_CSS}
:host { display: block; }
.pfs-entry {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}
.pfs-entry__dot {
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 50%;
  background: var(--pfs-color-outline-strong);
}
.pfs-entry[data-status="loading"] .pfs-entry__dot {
  background: var(--pfs-color-warning);
  animation: pfs-pulse 1.4s infinite;
}
.pfs-entry[data-status="ok"] .pfs-entry__dot { background: var(--pfs-color-success); }
.pfs-entry[data-status="error"] .pfs-entry__dot { background: var(--pfs-color-danger); }
.pfs-entry__label { flex: 1; color: var(--pfs-color-on-surface); }
.pfs-entry__version { color: var(--pfs-color-on-surface-muted); font-variant-numeric: tabular-nums; }
@keyframes pfs-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
`;
